import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

const FLAG_BY_LANGUAGE = {
  en: '/lang/en.svg',
  fr: '/lang/fr.svg',
};

function FlagPlaceholder({ code, active = false }) {
  const normalizedCode = String(code || '').toLowerCase();
  const flagSrc = FLAG_BY_LANGUAGE[normalizedCode];

  if (flagSrc) {
    return (
      <span
        className={`inline-flex h-4 w-6 items-center justify-center overflow-hidden rounded-[3px] border ${
          active ? 'border-[var(--dash-other)]/70' : 'border-white/35'
        }`}
        aria-hidden="true"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={flagSrc}
          alt=""
          width="24"
          height="16"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex h-4 w-6 items-center justify-center rounded-[3px] border text-[8px] font-bold leading-none ${
        active
          ? 'border-[var(--dash-other)]/70 bg-[var(--dash-other)]/20 text-[var(--dash-other)]'
          : 'border-white/35 bg-white/10 text-white/80'
      }`}
      aria-hidden="true"
    >
      {String(code || '').slice(0, 2).toUpperCase() || '--'}
    </span>
  );
}

export default function HeaderLanguageSelect({
  value,
  options = [],
  onChange,
  className = '',
  triggerClassName = '',
  panelClassName = '',
  adminStyle = false,
}) {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => {
        if (option && typeof option === 'object') {
          return {
            label: String(option.label ?? option.value ?? ''),
            value: option.value,
          };
        }
        return {
          label: String(option ?? ''),
          value: option,
        };
      }),
    [options]
  );

  const selected = normalizedOptions.find((item) => item.value === value);

  useEffect(() => {
    function onPointerDown(event) {
      if (!containerRef.current || containerRef.current.contains(event.target)) return;
      setIsOpen(false);
    }

    function onEscape(event) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen) return;
    const active = document.activeElement;
    if (panelRef.current && active && panelRef.current.contains(active)) {
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`group flex h-10 w-full items-center justify-between border transition-all duration-300 focus-visible:outline-none ${
          adminStyle
            ? 'rounded-lg border-[var(--dash-border-strong)] bg-[var(--dash-surface)] px-3 text-xs font-medium text-[var(--dash-text)] hover:bg-[var(--dash-bg)] focus-visible:border-[var(--dash-border-strong)] focus-visible:ring-0 shadow-none'
            : 'rounded-full border-white/20 bg-[linear-gradient(135deg,rgba(255,255,255,0.16),rgba(255,255,255,0.05))] px-3 text-left text-xs font-semibold tracking-[0.08em] text-white backdrop-blur-md hover:border-[var(--dash-other)] hover:text-[var(--dash-other)] focus-visible:border-[var(--dash-other)] focus-visible:ring-0'
        } ${triggerClassName}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {adminStyle ? (
          <>
            <span className="inline-flex min-w-0 items-center gap-2">
              <FlagPlaceholder code={selected?.value} active={isOpen} />
              <span className="leading-none text-[10px] font-semibold">{selected?.label || ''}</span>
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-[var(--dash-text-muted)] transition-transform duration-300 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-2">
              <FlagPlaceholder code={selected?.value} active={isOpen} />
              <span className="leading-none">{selected?.label || ''}</span>
            </span>
            <ChevronDown
              className={`h-4 w-4 text-white/80 transition-transform duration-300 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </>
        )}
      </button>

      <div
        ref={panelRef}
        className={`absolute right-0 z-50 mt-2 min-w-full origin-top-right overflow-hidden rounded-xl border p-1 backdrop-blur-xl transition-all duration-200 ${
          adminStyle
            ? 'border-[var(--dash-border)] bg-[var(--dash-surface)] shadow-none'
            : 'border-white/15 bg-[rgba(8,8,10,0.96)] shadow-[0_20px_50px_rgba(0,0,0,0.45)]'
        } ${
          isOpen
            ? 'visible translate-y-0 scale-100 opacity-100'
            : 'pointer-events-none invisible -translate-y-1 scale-[0.98] opacity-0'
        } ${panelClassName}`}
        role="listbox"
        inert={!isOpen ? true : undefined}
      >
        {normalizedOptions.map((option) => {
          const isActive = option.value === value;
          return (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => {
                onChange?.(option.value);
                setIsOpen(false);
              }}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold tracking-[0.08em] transition-colors ${
                adminStyle
                  ? isActive
                    ? 'bg-[var(--dash-primary)]/10 text-[var(--dash-primary)]'
                    : 'text-[var(--dash-text-soft)] hover:bg-[var(--dash-surface-2)] hover:text-[var(--dash-text)] focus-visible:outline-none focus-visible:bg-[var(--dash-surface-2)] focus-visible:text-[var(--dash-text)]'
                  : isActive
                  ? 'bg-[var(--dash-other)]/20 text-[var(--dash-other)]'
                  : 'text-white/85 hover:bg-[var(--dash-other)]/15 hover:text-[var(--dash-other)] focus-visible:outline-none focus-visible:bg-[var(--dash-other)]/15 focus-visible:text-[var(--dash-other)]'
              }`}
              tabIndex={isOpen ? 0 : -1}
              role="option"
              aria-selected={isActive}
            >
              <span className="inline-flex items-center gap-2">
                <FlagPlaceholder code={option.value} active={isActive} />
                <span>{option.label}</span>
              </span>
              {isActive ? <Check className="h-3.5 w-3.5" /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
